import React, { useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Collapse from "@material-ui/core/Collapse";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import NotesOutlinedIcon from "@material-ui/icons/NotesOutlined";
import TimelineIcon from "@material-ui/icons/Timeline";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";

const drawerWidth = 330;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		border: 0,
		borderLeft: `1px solid ${theme.palette.divider}`,
		borderRadius: 0,
		backgroundColor: theme.palette.background.paper,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		alignItems: "center",
		padding: theme.spacing(0.75, 1.25),
		minHeight: "72px",
		justifyContent: "space-between",
		"& .MuiTypography-root": { fontWeight: 780, fontSize: ".94rem" },
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.paper,
		flexDirection: "column",
		padding: theme.spacing(0, 2, 2),
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: theme.spacing(1, 0),
		width: 72,
		height: 72,
		boxShadow: "0 8px 22px rgba(30,22,62,.14)",
	},

	contactHeader: {
		display: "flex",
		padding: theme.spacing(2.25, 1, 2),
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		border: 0,
		"& > *": { margin: 3 },
		"& .contact-name": { fontWeight: 800, fontSize: "1.08rem" },
	},

	contactDetails: {
		marginTop: 0,
		padding: theme.spacing(2, 0),
		display: "flex",
		flexDirection: "column",
		border: 0,
	},
	infoToggle: {
		width: "100%",
		justifyContent: "space-between",
		padding: "9px 10px",
		margin: "8px 0 2px",
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 9,
		fontWeight: 750,
		textTransform: "none",
		"& .MuiButton-endIcon": { transition: "transform 160ms ease" },
	},
	infoToggleOpen: {
		"& .MuiButton-endIcon": { transform: "rotate(180deg)" },
	},
	contactExtraInfo: {
		marginTop: 8,
		padding: 11,
		borderRadius: 10,
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#faf9fc",
	},
	sectionTitle: { fontWeight: 780, marginBottom: 10 },
	infoRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 0", color: theme.palette.text.secondary },
	infoIcon: { fontSize: 19, color: theme.palette.primary.main },
	channelChip: { marginTop: 6, backgroundColor: "#eaf8ef", color: "#137a3b", fontWeight: 750 },
	editButton: { marginTop: 10, borderRadius: 10 },
	crmSection: { borderTop: `1px solid ${theme.palette.divider}`, padding: theme.spacing(1, 0, 0) },
	crmSectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: theme.spacing(0.55, 0), "& .MuiTypography-root": { fontWeight: 750, fontSize: ".78rem" } },
	crmSectionBody: { padding: theme.spacing(0.4, 0, 0.8) },
	leadSelect: { width: "100%", border: `1px solid ${theme.palette.divider}`, borderRadius: 7, padding: "6px 8px", background: theme.palette.background.paper, color: theme.palette.text.primary, fontSize: ".76rem" },
	crmChips: { display: "flex", flexWrap: "wrap", gap: 5 },
	crmChip: { height: 23, fontSize: ".68rem" },
	noteBox: { width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: 52, border: `1px solid ${theme.palette.divider}`, borderRadius: 7, padding: 7, fontSize: ".73rem", fontFamily: "inherit", background: theme.palette.background.paper, color: theme.palette.text.primary },
	activityRow: { display: "flex", alignItems: "center", gap: 7, padding: "4px 0", fontSize: ".7rem", color: theme.palette.text.secondary },
	activityIcon: { fontSize: 15, color: theme.palette.primary.main },
	fileRow: { display: "flex", gap: 6, alignItems: "center", fontSize: ".72rem", color: theme.palette.text.secondary },
	assignmentGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [detailsExpanded, setDetailsExpanded] = useState(true);
	const [sections, setSections] = useState({ lead: true, tags: true, notes: true, activity: true, files: true, assignment: true });
	const toggleSection = name => setSections(current => ({ ...current, [name]: !current[name] }));

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<Typography>Detalles del contacto</Typography>
				<IconButton onClick={handleDrawerClose} aria-label="Cerrar detalles"><CloseIcon /></IconButton>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper elevation={0} className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography className="contact-name">{contact.name}</Typography>
						<Chip size="small" icon={<WhatsAppIcon />} label="WhatsApp" className={classes.channelChip} />
						<Button
							variant="outlined"
							color="primary"
							startIcon={<EditOutlinedIcon />}
							className={classes.editButton}
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Divider />
					<Button
						variant="outlined"
						className={`${classes.infoToggle} ${detailsExpanded ? classes.infoToggleOpen : ""}`}
						onClick={() => setDetailsExpanded((expanded) => !expanded)}
						endIcon={<ExpandMoreIcon />}
					>
						Información del contacto
					</Button>
					<Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
					<Paper elevation={0} className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<Typography className={classes.sectionTitle}>Información</Typography>
						<div className={classes.infoRow}><PhoneOutlinedIcon className={classes.infoIcon} /><Link href={`tel:${contact.number}`}>{contact.number || "Sin teléfono"}</Link></div>
						<div className={classes.infoRow}><EmailOutlinedIcon className={classes.infoIcon} /><Typography variant="body2">{contact.email || "Sin correo electrónico"}</Typography></div>
						{contact?.extraInfo?.length > 0 && <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>Campos personalizados</Typography>}
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("lead")}><Typography>Estado del lead</Typography><ExpandMoreIcon style={{ transform: sections.lead ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.lead}><div className={classes.crmSectionBody}><select className={classes.leadSelect} defaultValue="Cliente potencial" aria-label="Estado del lead"><option>Cliente potencial</option><option>Interesado</option><option>Cliente</option><option>Sin clasificar</option></select></div></Collapse></div>
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("tags")}><Typography><LabelOutlinedIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 5 }} />Etiquetas</Typography><ExpandMoreIcon style={{ transform: sections.tags ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.tags}><div className={`${classes.crmSectionBody} ${classes.crmChips}`}><Chip size="small" label="Interesado" className={classes.crmChip} /><Chip size="small" label="Clases" className={classes.crmChip} /><Chip size="small" label="WhatsApp" color="primary" className={classes.crmChip} /></div></Collapse></div>
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("notes")}><Typography><NotesOutlinedIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 5 }} />Notas</Typography><ExpandMoreIcon style={{ transform: sections.notes ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.notes}><div className={classes.crmSectionBody}><textarea className={classes.noteBox} defaultValue={`Interesada en dar clases grupales.\nPrefiere horarios en la mañana.`} aria-label="Notas del contacto" /></div></Collapse></div>
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("activity")}><Typography><TimelineIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 5 }} />Actividad</Typography><ExpandMoreIcon style={{ transform: sections.activity ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.activity}><div className={classes.crmSectionBody}><div className={classes.activityRow}><WhatsAppIcon className={classes.activityIcon} /> Conversación iniciada <span style={{ marginLeft: "auto" }}>Hoy, 10:15</span></div><div className={classes.activityRow}><EmailOutlinedIcon className={classes.activityIcon} /> Mensaje recibido <span style={{ marginLeft: "auto" }}>Hoy, 10:15</span></div><div className={classes.activityRow}><PhoneOutlinedIcon className={classes.activityIcon} /> Mensaje enviado <span style={{ marginLeft: "auto" }}>Hoy, 10:20</span></div></div></Collapse></div>
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("files")}><Typography><FolderOutlinedIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 5 }} />Archivos compartidos</Typography><ExpandMoreIcon style={{ transform: sections.files ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.files}><div className={classes.crmSectionBody}><div className={classes.fileRow}><FolderOutlinedIcon className={classes.activityIcon} /> Fotos&nbsp;&nbsp; Videos&nbsp;&nbsp; Documentos</div></div></Collapse></div>
						<div className={classes.crmSection}><div className={classes.crmSectionHeader} onClick={() => toggleSection("assignment")}><Typography><AssignmentIndOutlinedIcon style={{ fontSize: 15, verticalAlign: "middle", marginRight: 5 }} />Asignación y seguimiento</Typography><ExpandMoreIcon style={{ transform: sections.assignment ? "rotate(180deg)" : "none" }} /></div><Collapse in={sections.assignment}><div className={`${classes.crmSectionBody} ${classes.assignmentGrid}`}><select className={classes.leadSelect} defaultValue="Camila Gómez" aria-label="Asignado a"><option>Camila Gómez</option><option>Sin asignar</option></select><label style={{ fontSize: ".68rem" }}><EventOutlinedIcon style={{ fontSize: 15, verticalAlign: "middle" }} /> Fecha de seguimiento<input type="date" className={classes.leadSelect} /></label></div></Collapse></div>
					</Paper>
					</Collapse>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
